import { useMemo, useState } from 'react';
import useStrategyDashboardStore from '../Stores/strategyDashboardStore';
import {
  ACCE_KELLY_PARAMS,
  calculateAcceKellyContracts,
} from '../../services/strategyStats/acceKellyService';
import {
  buildRecentTradeRows,
  buildStrategyRecentStats,
} from '../../services/strategyStats/strategyStatsService';
import { formatCurrency } from '../../services/strategyStats/strategyFormatters';
import '../Styles/ACCEPage.css';

export default function ACCEPage() {
  const accePayload = useStrategyDashboardStore(
    state => state.strategies?.acce
  );

  const acceRecentTrades = accePayload?.recent_trades || [];

  const acceStats = useMemo(
    () => buildStrategyRecentStats(acceRecentTrades, { limit: 25 }),
    [acceRecentTrades]
  );

  const acceTradeRows = useMemo(
    () => buildRecentTradeRows(acceRecentTrades, { limit: 10 }),
    [acceRecentTrades]
  );

  const [currentEquity, setCurrentEquity] = useState(
    ACCE_KELLY_PARAMS.referenceCapital
  );

  const sizingResult = useMemo(
    () => calculateAcceKellyContracts(currentEquity),
    [currentEquity]
  );

  function handleEquityChange(event) {
    const value = event.target.value;

    if (value === '') {
      setCurrentEquity('');
      return;
    }

    setCurrentEquity(value);
  }

  return (
    <main className="accePage">
      <section className="accePageGrid">
        <div className="acceLeftColumn">
          <section className="acceHeroContent">
            <div className="acceEyebrow">Capital Growth Entry Program</div>

            <h1>ACCE</h1>

            <h2>Adaptive Capital &amp; Convexity Engine</h2>

            <p>
              ACCE is the aggressive entry point into the SPX Gamma system — built to
              generate capital from a smaller starting account through selective,
              defined-risk SPX premium selling.
            </p>

            <p>
              The program is selective by design. It waits for market structure,
              volatility behavior, and dealer positioning to align before deploying
              capital. As equity grows, contract size scales up; during drawdowns,
              exposure steps down. The goal is aggressive capital generation without
              abandoning structure, discipline, or defined risk.
            </p>

            <p>
              When ACCE triggers, it takes priority over GEMIC. The system treats an ACCE
              setup as the higher-conviction capital-growth opportunity, so available
              strategy capital should be directed to ACCE first rather than split across
              overlapping 1-DTE structures.
            </p>

            <div className="acceHeroActions">
              <a
                className="accePrimaryButton"
                href="https://spxgamma-acce-ic-framework.spxgamma.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Full ACCE Framework
              </a>
            </div>
          </section>

          <section className="accePanel">
            <div className="accePanelHeader">
              <div>
                <span className="acceSectionLabel">Performance</span>
                <h3>Recent ACCE Statistics</h3>
              </div>

              <span className="accePanelPill">Last 25 Trades</span>
            </div>

            <div className="acceStatsGrid">
              {acceStats.map(stat => (
                <div
                  key={stat.label}
                  className={`acceStatCard acceStatCard--${stat.tone}`}
                >
                  <span>{stat.label}</span>
                  <strong>{stat.value}</strong>
                  <small>{stat.helper}</small>
                </div>
              ))}
            </div>
          </section>

          <section className="accePanel">
            <div className="accePanelHeader">
              <div>
                <span className="acceSectionLabel">Trade History</span>
                <h3>Latest 10 Closed Trades</h3>
                <p className="accePanelSubtext">
                  Displaying the latest 10 closed trades. Statistics above are calculated from the latest 25 trades.
                </p>
              </div>
            </div>

            <div className="acceTradeTableWrap">
              <table className="acceTradeTable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Expiration</th>
                    <th>Structure</th>
                    <th>Credit</th>
                    <th>Result</th>
                    <th>P/L</th>
                  </tr>
                </thead>

                <tbody>
                  {acceTradeRows.length > 0 ? (
                    acceTradeRows.map((trade, index) => (
                      <tr key={trade.id || `${trade.date}-${index}`}>
                        <td>{trade.date}</td>
                        <td>{trade.expiration}</td>
                        <td>{trade.structure}</td>
                        <td>{trade.credit}</td>
                        <td>
                          <span
                            className={`acceResultPill acceResultPill--${trade.tone}`}
                          >
                            {trade.result}
                          </span>
                        </td>
                        <td className={`accePnlCell accePnlCell--${trade.tone}`}>
                          {trade.pnl}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="acceEmptyTableCell">
                        No closed ACCE trades available yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <aside className="acceRightColumn">
          <section className="accePanel acceCalculatorPanel">
            <div className="accePanelHeader">
              <div>
                <span className="acceSectionLabel">Sizing Tool</span>
                <h3>ACCE Contract Scaling</h3>
              </div>
            </div>

            <p className="acceCalculatorIntro">
              Enter the current ACCE program equity to calculate the model-based
              contract size.
            </p>

            <label className="acceInputLabel" htmlFor="acce-current-equity">
              Current ACCE Equity
            </label>

            <div className="acceInputShell">
              <span>$</span>
              <input
                id="acce-current-equity"
                type="number"
                min="0"
                step="100"
                value={currentEquity}
                onChange={handleEquityChange}
                placeholder="10000"
              />
            </div>

            <div className="acceSizingOutput">
              <span>Model Contract Size</span>

              <strong>
                {sizingResult.modelContracts}{' '}
                {sizingResult.modelContracts === 1 ? 'Contract' : 'Contracts'}
              </strong>

              <small>
                Based on {formatCurrency(sizingResult.currentCapital)} current
                ACCE equity.
              </small>
            </div>

            {sizingResult.capped && (
              <div className="acceSizingNotice">
                Model size is capped at 20 contracts.
              </div>
            )}

            <div className="acceSizingMeta">
              <div>
                <span>Base Equity</span>
                <strong>$10,000</strong>
              </div>

              <div>
                <span>Base Size</span>
                <strong>4 Contracts</strong>
              </div>
            </div>

            <p className="acceSizingDisclaimer">
              This tool displays model sizing based on program equity. It is not a
              recommendation to buy or sell any security.
            </p>
          </section>

          <section className="accePanel acceInfoStack">
            <div className="acceInfoBlock">
              <span>Designed For</span>
              <p>
                Built for accounts starting around $10,000, with a $5,000 minimum
                equity threshold, seeking a rules-based path to aggressive
                compounding.
              </p>
            </div>

            <div className="acceInfoBlock">
              <span>Scaling Logic</span>
              <p>
                Contract size adapts to the program equity curve, increasing as
                capital grows and stepping down during drawdowns.
              </p>
            </div>

            <div className="acceInfoBlock">
              <span>Objective</span>
              <p>
                Path-dependent growth through selective, high-impact capital
                deployment when market structure supports premium selling.
              </p>
            </div>
          </section>
        </aside>
      </section>
      <section className="acceDisclaimer">
        ACCE trade posts are for educational and informational purposes only.
        They are not financial advice or a recommendation to buy or sell any
        security or options contract. Options trading involves risk and may not
        be suitable for all traders.
      </section>
    </main>
  );
}
